// client/src/pages/PaymentSuccess.jsx
import React, { useEffect, useContext, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2, CheckCircle, AlertCircle, ArrowRight, Building2 } from 'lucide-react';
import { OnboardingContext } from '../contexts/OnboardingContext';
import api from '../utils/api';
import OnboardGuard from '../components/OnboardGuard';

/** Local UI components */
const Logo = ({ size = 'md', className = '' }) => {
  const sizes = {
    sm: 'h-12',
    md: 'h-20',
    lg: 'h-64',
    xl: 'max-h-[420px]',
  };
  return (
    <img
      src="/STB_LOGO_V1.png"
      alt="SecureTheBag Logo"
      className={`${sizes[size] || sizes.md} w-auto object-contain ${className}`}
    />
  );
};

const Footer = () => (
  <footer className="mt-auto py-4 text-center text-sm text-gray-500">
    © {new Date().getFullYear()} SecureTheBag
  </footer>
);

function PaymentSuccessForm() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [status, setStatus] = useState('loading'); // 'loading', 'success', 'error'
  const [error, setError] = useState('');
  const { setOnboardingStepIfNotComplete } = useContext(OnboardingContext);

  useEffect(() => {
    let isMounted = true;

    if (!sessionId) {
      setStatus('error');
      setError('Missing session ID.');
      return;
    }

    (async () => {
      try {
        const res = await api.get(`/api/payments/session?session_id=${encodeURIComponent(sessionId)}`);
        const session = res.data.session;
        // Trial checkouts return payment_status='no_payment_required' (no charge taken at trial start),
        // so we gate on session.status==='complete' + a created subscription rather than payment_status==='paid'.
        const completed = session?.status === 'complete' && !!session?.subscription;
        if (completed) {
          await api.post('/api/user/onboarding', { step: 'connect_bank' });
          if (!isMounted) return;
          setOnboardingStepIfNotComplete('connect_bank');
          try { sessionStorage.removeItem('selectedPriceId'); } catch {}
          setStatus('success');
        } else {
          if (!isMounted) return;
          setStatus('error');
          setError('We could not confirm the checkout. Please try again.');
        }
      } catch (err) {
        console.error('Failed to retrieve session:', err);
        if (!isMounted) return;
        setStatus('error');
        setError('Failed to confirm payment.');
      }
    })();

    return () => {
      isMounted = false;
    };
  }, [sessionId, setOnboardingStepIfNotComplete]);

  const handleConnectBank = () => {
    navigate('/connect-bank');
  };

  if (status === 'loading') {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white flex flex-col">
        <div className="flex-1 flex items-center justify-center p-4 sm:p-6">
          <div className="w-full max-w-md">
            {/* Header */}
            <div className="text-center mb-8">
              <div className="inline-block mb-6">
                <Logo size="md" />
              </div>
              <div className="flex items-center justify-center gap-3 mb-4">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                  <CheckCircle className="w-6 h-6 text-blue-600" />
                </div>
              </div>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">Verifying Payment</h1>
              <p className="text-gray-600">Please wait while we confirm your payment...</p>
            </div>

            {/* Loading Animation */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-8">
              <div className="flex flex-col items-center justify-center">
                <Loader2 className="w-12 h-12 text-blue-600 animate-spin mb-4" />
                <p className="text-gray-600 text-center">Confirming your subscription</p>
              </div>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }
  
  if (status === 'error') {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white flex flex-col">
        <div className="flex-1 flex items-center justify-center p-4 sm:p-6">
          <div className="w-full max-w-md">
            {/* Header */}
            <div className="text-center mb-8">
              <div className="inline-block mb-6">
                <Logo size="md" />
              </div>
              <div className="flex items-center justify-center gap-3 mb-4">
                <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                  <AlertCircle className="w-6 h-6 text-red-600" />
                </div>
              </div>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">Payment Verification Failed</h1>
            </div>

            {/* Error Card */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6 mb-6">
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 sm:p-4 mb-6">
                <p className="text-red-800 text-center">{error}</p>
              </div>
              <button
                onClick={() => navigate('/choose-plan')}
                className="w-full min-h-[44px] px-6 py-3 bg-gray-900 hover:bg-gray-800 text-white font-semibold rounded-lg transition-all duration-200"
              >
                Return to Plan Selection
              </button>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }
  
  // status === 'success'
  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white flex flex-col">
      <div className="flex-1 flex items-center justify-center p-4 sm:p-6">
        <div className="w-full max-w-md">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="inline-block mb-6">
              <Logo size="md" />
            </div>
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                <CheckCircle className="w-6 h-6 text-green-600" />
              </div>
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Payment Successful!</h1>
            <p className="text-gray-600">Your subscription is now active</p>
          </div>

          {/* Success Card */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6 mb-6">
            {/* Success Message */}
            <div className="bg-green-50 border border-green-200 rounded-lg p-3 sm:p-4 mb-6">
              <p className="text-green-800 text-center font-medium">
                Thank you! Your payment has been processed successfully.
              </p>
            </div>

            {/* Next Step */}
            <div className="mb-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-2">Next Step</h2>
              <p className="text-gray-600 text-base sm:text-sm">
                Connect your bank account securely with Plaid to start monitoring your balance and receiving notifications.
              </p>
            </div>

            {/* Continue Button */}
            <button
              onClick={handleConnectBank}
              className="w-full min-h-[44px] px-6 py-3 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-lg transition-all duration-200 flex items-center justify-center gap-2 shadow-md hover:shadow-lg"
            >
              <Building2 className="w-5 h-5" />
              Connect Bank Account
              <ArrowRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}

// /payment-success is a transient handoff page — the user's step is still 'choose_plan'
// until either the subscription webhook or the POST inside PaymentSuccessForm advances it.
// Guarding on 'payment_success' previously broke the funnel because that step does not
// exist in ONBOARDING_STEPS and OnboardGuard bounced users back to /choose-plan.
export default function PaymentSuccess() {
  return (
    <OnboardGuard currentStep="choose_plan">
      <PaymentSuccessForm />
    </OnboardGuard>
  );
}